import * as addressLookup from '../../services/address-lookup.js';
import * as checkoutApi from '../../services/checkout-api.js';
import * as delivery from '../../services/delivery.js';
import * as formInput from '../../services/form-input.js';
import * as orderSubmission from '../../services/order-submission.js';
import * as recipientImporter from '../../services/recipient-importer.js';
import * as stripe from '../../services/stripe.js';
import { createTotalManager } from '../../services/totals.js';

export default () => ({
    checkout: {
        addressFieldsVisible: true,
        corporateToken: '',
        currentStep: 'vouchers',
        failed: { data: { messages: [], next_url: null, status: '' }, status: false },
        form: null,
        placementStage: 'placing',
        promoCode: { error: '', pending: false, value: '' },
        ready: true,
        session: null,
        steps: [
            { name: 'vouchers', label: 'Voucher', complete: false },
            { name: 'delivery', label: 'Delivery', complete: false },
            { name: 'recipients', label: 'Recipients', complete: false },
            { name: 'billing', label: 'Billing', complete: false },
        ],
        stripe: { confirmationToken: '', errorMessage: '', paymentIntent: '', paymentMethod: null },
        termsAccepted: false,
        totalUpdating: false,
    },

    contact: {
        email: '',
        firstName: '',
        lastName: '',
        telephone: '',
        title: '',
    },

    delivery: {
        address: '',
        address1: '',
        address2: '',
        address3: '',
        country: '',
        county: '',
        firstName: '',
        lastName: '',
        postcode: '',
        title: '',
        town: '',
    },

    importModal: {
        active: false,
        error: null,
        processing: false,
        recipients: { count: 0, data: [] },
    },

    messageModal: {
        active: false,
        data: { message: ['', '', '', '', ''], name: '', personalisation: 'custom', salutation: '' },
        recipientIndex: null,
    },

    order: {
        deliveryMethod: 'email',
        packaging: null,
        placed: false,
        recipients: { count: 0, data: [] },
        requiresPosting: false,
        voucher: null,
        voucherOption: null,
        voucherStyle: '',
    },

    settings: {},

    totals: { discount: 0, full: 0 },

    vouchers: [],

    _currencyFormatter: null,
    _totalUpdateTimeout: null,

    init() {
        const gifted = window.Gifted;

        this.checkout.form = this.$root.querySelector('form[name="corporateOrder"]');
        this._totalManager = createTotalManager({
            getBasketPayload: () => this.buildBasketPayload(),
            getEmail: () => this.contact.email,
            getForm: () => this.checkout.form,
            getSessionId: () => this.checkout.session.id,
            getSessionType: () => this.checkout.session.type,
            getStripeAmount: (totals) => totals.full,
            onPromoError: (error) => { this.checkout.promoCode.error = error; },
            onUpdate: (totals) => {
                this.checkout.totalUpdating = false;
                this.totals = totals;
            },
        });
        this.checkout.session = gifted.checkoutSession;
        this.checkout.corporateToken = gifted.corporateToken ?? '';
        this.settings = gifted.settings;
        this.delivery.country = this.settings.default_country || '';
        this.vouchers = gifted.vouchers;

        if (gifted.preselectedVoucherId) {
            const preselected = this.vouchers.find(v => v.id === gifted.preselectedVoucherId);

            if (preselected) {
                this.order.voucher = preselected;
                this.checkout.steps = this.checkout.steps.filter(step => step.name !== 'vouchers');
                this.checkout.currentStep = this.checkout.steps[0]?.name ?? 'delivery';
            }
        }

        this.order.recipients.count = this.settings.min_recipients;
        this.order.voucherStyle = this.settings.default_voucher_style;

        this._currencyFormatter = new Intl.NumberFormat(this.settings.locale, {
            currency: this.settings.currency,
            currencyDisplay: 'symbol',
            style: 'currency',
        });

        this.updateRecipientCount();

        this.checkout.addressFieldsVisible = this.shouldShowAddressFields();

        if (!this.checkout.addressFieldsVisible) {
            addressLookup.init('#delivery_address', {
                onFailed: () => this.showAddressFields(),
                onSuccess: (address) => this.handleAddressFound(address),
            });
        }

        stripe.mountElement('#payment-element');

        this.$watch('order.deliveryMethod', () => {
            this.order.requiresPosting = (this.order.deliveryMethod === 'post');
            this.updateTotal();
        });

        this.$watch('order.packaging', () => this.updateTotal());
        this.$watch('order.voucherStyle', () => this.updateTotal());

        this.$watch('messageModal.data.personalisation', (value) => {
            if (value === 'default' && this.messageModal.data) {
                const defaultRecipient = this.order.recipients.data[0];

                this.messageModal.data.message = Array.from(defaultRecipient.message);
                this.messageModal.data.salutation = defaultRecipient.salutation;
            }
        });

        for (let i = 0; i < 5; i++) {
            this.$watch(`messageModal.data.message[${i}]`, (newVal, oldVal) => {
                if (this.messageModal.data && this.getMessageLength(this.messageModal.data.message) > this.settings.message_limit) {
                    this.messageModal.data.message[i] = oldVal;
                }
            });
        }

        formInput.init(this.$root);
    },

    // Step navigation

    changeStep(name) {
        name = name.toLowerCase();

        const nextStep = this.checkout.steps.find(step => step.name === name);

        if (!nextStep) return;

        const stepIndex = this.checkout.steps.indexOf(nextStep);
        const previousStep = stepIndex > 0 ? this.checkout.steps[stepIndex - 1] : null;

        if (previousStep && !previousStep.complete) return;

        this.checkout.currentStep = nextStep.name;
        this.checkout.failed = { data: { messages: [], next_url: null, status: '' }, status: false };

        document.querySelector('.js-corporate-order')?.scrollIntoView();
    },

    getNextStepName() {
        const currentStep = this.checkout.steps.find(step => step.name === this.checkout.currentStep);
        const nextStep = this.checkout.steps[this.checkout.steps.indexOf(currentStep) + 1];

        return nextStep ? nextStep.name : '';
    },

    validateStep(stepName) {
        const fieldset = this.checkout.form.querySelector(`[name="${stepName}"]`);

        if (!fieldset) {
            this.checkout.steps.find(step => step.name === stepName).complete = true;
            this.changeStep(this.getNextStepName());
            return;
        }

        const invalids = fieldset.querySelectorAll(':invalid');

        if (invalids.length > 0) {
            invalids.forEach(el => el.dataset.touched = 'true');

            if (stepName === 'delivery' && this.order.requiresPosting && !this.checkout.addressFieldsVisible) {
                this.showAddressFields();
            }

            this.showFirstError();
            return;
        }

        this.checkout.steps.find(step => step.name === stepName).complete = true;

        if (stepName === 'billing') {
            this.placeOrder();
        } else {
            this.changeStep(this.getNextStepName());
        }
    },

    showFirstError() {
        setTimeout(() => {
            const errorEl = this.checkout.form.querySelector(`[name="${this.checkout.currentStep}"] :invalid`);

            if (errorEl) {
                errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    },

    // Voucher selection

    selectVoucher(voucher, option = null) {
        if (voucher.options.length && !option) {
            voucher.show_options = true;
            return;
        }

        this.order.voucher = voucher;

        if (voucher.packaging.length) {
            this.order.packaging = voucher.packaging[0];
        }

        if (option) {
            this.order.voucherOption = option;
        }

        this.checkout.steps.find(step => step.name === 'vouchers').complete = true;
        this.changeStep('delivery');
        this.updateTotal();
    },

    // Delivery + Address

    updatePostageOptions() {
        // no-op: corporate orders do not have postage option filtering
    },

    handleAddressFound(address) {
        delivery.populateAddressFields(this.delivery, address);
        this.updatePostageOptions();

        this.showAddressFields();

        setTimeout(() => {
            this.$root.querySelectorAll('.js-address-fields input').forEach(element => {
                formInput.hidePlaceholder(element);
            });
        }, 100);
    },

    showAddressFields() {
        this.checkout.addressFieldsVisible = true;
    },

    shouldShowAddressFields() {
        if (!this.settings.features?.address_lookup) return true;
        if (this.settings.features?.self_fulfilment) return true;

        const supportedCountries = ['GB', 'US'];

        return !this.settings.delivery_countries.some(country => supportedCountries.includes(country));
    },

    async showVoucherPreview() {
        const modal = document.getElementById('voucherPreview');

        if (!modal) return;

        const iframe = modal.querySelector('iframe');
        const img = modal.querySelector('img');

        if (iframe) iframe.src = '';
        if (img) img.src = img.dataset.placeholder;

        modal.removeAttribute('data-type');
        window.jQuery(modal).foundation('reveal', 'open');

        const preview = await checkoutApi.previewVoucher({
            delivery: 'email',
            message: ['This is a sample message to show you how the email voucher will look.'],
            name: 'Guest',
            option_id: this.order.voucherOption ? this.order.voucherOption.id : null,
            prefix: 'Dear',
            voucher_id: this.order.voucher.id,
            voucher_style: this.order.voucherStyle,
        });

        modal.setAttribute('data-type', preview.type);

        if (preview.type === 'iframe') {
            if (iframe) iframe.src = preview.data;
        } else {
            if (img) img.src = 'data:image/jpeg;base64,' + preview.data;
        }
    },

    // Recipients

    updateRecipientCount(modifier = null) {
        const previousCount = this.order.recipients.data.length;
        let count = this.order.recipients.count;

        if (modifier) {
            count += modifier;
        }

        count = Math.max(this.settings.min_recipients, Math.min(this.settings.max_recipients, count));

        this.order.recipients.count = count;

        if (count === previousCount) return;

        if (count > previousCount) {
            for (let i = 0; i < count - previousCount; i++) {
                this.order.recipients.data.push({
                    email: '',
                    message: [],
                    name: '',
                    personalisation: 'default',
                    salutation: '',
                });
            }
        } else if (count < previousCount) {
            this.order.recipients.data.splice(count, previousCount - count);
        }

        if (this.order.recipients.data.length > 0) {
            this.order.recipients.data[0].personalisation = 'custom';
        }

        this.updateTotal();
    },

    recipientsWithDefaultMessage() {
        return this.order.recipients.data.filter(r => r.personalisation === 'default');
    },

    getUniqueEmailRecipients() {
        if (this.order.deliveryMethod !== 'email') return 0;

        const uniqueEmails = new Set();

        this.order.recipients.data.forEach(r => {
            if (r.email) uniqueEmails.add(r.email);
        });

        return uniqueEmails.size;
    },

    // Message modal

    editMessage(index) {
        if (!this.order.voucher?.can_personalise) return;

        const recipient = this.order.recipients.data[index];
        const defaultRecipient = this.order.recipients.data[0];

        this.messageModal.active = true;
        this.messageModal.recipientIndex = index;
        this.messageModal.data = {
            message: (recipient.personalisation === 'custom') ? Array.from(recipient.message) : Array.from(defaultRecipient.message),
            name: recipient.name,
            personalisation: recipient.personalisation,
            salutation: (recipient.personalisation === 'custom') ? recipient.salutation : defaultRecipient.salutation,
        };
    },

    updateMessage() {
        const index = this.messageModal.recipientIndex;
        const recipient = this.order.recipients.data[index];

        recipient.name = this.messageModal.data.name;
        recipient.personalisation = this.messageModal.data.personalisation;

        if (this.messageModal.data.personalisation === 'custom') {
            recipient.message = this.messageModal.data.message;
            recipient.salutation = this.messageModal.data.salutation;
        } else {
            recipient.message = [];
            recipient.salutation = '';
        }

        this.closeMessageModal();
    },

    closeMessageModal() {
        this.messageModal.active = false;
        this.messageModal.recipientIndex = null;
    },

    getMessageLength(message) {
        if (!message) return 0;

        return message.join('').length;
    },

    getMessageIndex(recipient, index) {
        if (!this.order.voucher?.can_personalise) return '';

        const defaultRecipient = this.order.recipients.data[0];

        return (recipient.personalisation === 'custom') ? recipient.message[index] : defaultRecipient.message[index];
    },

    getMessagePreview(recipient) {
        const defaultRecipient = this.order.recipients.data[0];
        const separator = recipient.name ? ',' : '';
        const message = (recipient.personalisation === 'custom') ? recipient.message : defaultRecipient.message;

        if (message.join('').length === 0) return '<em>No personalisation</em>';

        let salutation = '';

        if (recipient.name) {
            salutation = (recipient.personalisation === 'custom') ? recipient.salutation : defaultRecipient.salutation;
        }

        const text = `${salutation} ${recipient.name}${separator} ${message.join(' ')}`;

        const el = document.createElement('span');
        el.textContent = text;

        return el.innerHTML;
    },

    // Import modal

    openImportModal() {
        this.importModal.active = true;
    },

    closeImportModal() {
        this.importModal = {
            active: false,
            error: null,
            processing: false,
            recipients: { count: 0, data: [] },
        };

        const fileInput = document.querySelector('.js-import-modal input[type="file"]');

        if (fileInput) {
            fileInput.value = '';
        }
    },

    downloadCsvTemplate() {
        recipientImporter.downloadTemplate();
    },

    handleCsvUpload(file) {
        if (!file) return;

        this.importModal.error = null;
        this.importModal.processing = true;

        recipientImporter.parseFile(file, {
            max: this.settings.max_recipients,
            min: this.settings.min_recipients,
        }).then(recipients => {
            this.importModal.error = null;
            this.importModal.processing = false;
            this.importModal.recipients = { count: recipients.length, data: recipients };
        }).catch(error => {
            this.importModal.error = error;
            this.importModal.processing = false;
            this.importModal.recipients = { count: 0, data: [] };
        });
    },

    importRecipients() {
        this.order.recipients = { ...this.importModal.recipients };

        setTimeout(() => this.closeImportModal(), 100);

        this.updateTotal();
    },

    // Billing + Stripe

    formatCurrency(amount) {
        return this._currencyFormatter.format(amount);
    },

    getPostalAddress() {
        const nameFields = [
            this.delivery.title,
            this.delivery.firstName,
            this.delivery.lastName,
        ];

        const addressFields = [
            this.delivery.address1,
            this.delivery.address2,
            this.delivery.address3,
            this.delivery.town,
            this.delivery.county,
            this.delivery.postcode,
        ];

        const name = nameFields.filter(f => f && f.trim() !== '').join(' ');
        const address = addressFields.filter(f => f && f.trim() !== '').join(', ');

        return `${name}, ${address}`;
    },

    buildBasketPayload() {
        const packagingId = this.order.packaging && typeof this.order.packaging.id === 'number'
            ? this.order.packaging.id
            : null;
        const voucherOptionId = this.order.voucherOption && typeof this.order.voucherOption.id === 'number'
            ? this.order.voucherOption.id
            : null;

        return {
            corporate_token: this.checkout.corporateToken ?? '',
            delivery_method: this.order.deliveryMethod,
            packaging_id: packagingId,
            voucher_id: this.order.voucher?.id ?? null,
            voucher_option_id: voucherOptionId,
            voucher_style: this.order.voucherStyle,
            vouchers: Array.from({ length: this.order.recipients.count }, () => ({})),
        };
    },

    buildOrderPayload() {
        const defaultRecipient = this.order.recipients.data[0];

        return {
            ...this.buildBasketPayload(),
            contact_email: this.contact.email,
            contact_first_name: this.contact.firstName,
            contact_last_name: this.contact.lastName,
            contact_telephone: this.contact.telephone,
            contact_title: this.contact.title,
            delivery_address_1: this.delivery.address1,
            delivery_address_2: this.delivery.address2,
            delivery_address_3: this.delivery.address3,
            delivery_country: this.delivery.country,
            delivery_county: this.delivery.county,
            delivery_first_name: this.delivery.firstName,
            delivery_last_name: this.delivery.lastName,
            delivery_postcode: this.delivery.postcode,
            delivery_title: this.delivery.title,
            delivery_town: this.delivery.town,
            vouchers: this.order.recipients.data.map((recipient) => {
                const useDefault = recipient.personalisation === 'default';

                return {
                    email: recipient.email,
                    message: useDefault ? defaultRecipient.message : recipient.message,
                    name: recipient.name,
                    personalisation: recipient.personalisation,
                    salutation: useDefault ? defaultRecipient.salutation : recipient.salutation,
                };
            }),
        };
    },

    async updateTotal() {
        if (!this.order.voucher) {
            this.totals = { discount: 0, full: 0 };
            this.checkout.totalUpdating = false;
            return;
        }

        this.checkout.totalUpdating = true;
        this._totalManager.updateTotal();
    },

    async applyPromoCode() {
        this.checkout.promoCode.pending = true;
        await this._totalManager.applyPromoCode(this.checkout.promoCode.value);
        this.checkout.promoCode.pending = false;
    },

    async removePromoCode() {
        this.checkout.promoCode.pending = true;
        await this._totalManager.removePromoCode();
        this.checkout.promoCode.pending = false;
        this.checkout.promoCode.value = '';

        setTimeout(() => {
            const promoInput = this.$root.querySelector('[name="promocode"]');

            if (promoInput) {
                formInput.showPlaceholder(promoInput);
            }
        }, 100);
    },

    async placeOrder() {
        if (this.checkout.currentStep !== 'billing') return;

        this.checkout.ready = false;
        this.order.placed = true;
        this.checkout.placementStage = 'placing';

        if (this.totals.full === 0) {
            this.submitOrder();
            return;
        }

        const { confirmationToken, error } = await stripe.getToken({
            email: this.contact.email,
            name: `${this.contact.firstName} ${this.contact.lastName}`,
            phone: this.contact.telephone,
        });

        if (!confirmationToken || error) {
            this.checkout.ready = true;
            this.order.placed = false;

            if (error && error.type !== 'validation_error') {
                this.checkout.stripe.errorMessage = error.message;
            }

            return;
        }

        this.checkout.stripe.confirmationToken = confirmationToken.id;
        this.checkout.stripe.paymentMethod = confirmationToken.payment_method_preview;

        this.submitOrder();
    },

    async submitOrder() {
        const payload = this.buildOrderPayload();

        payload.checkout_session_type = this.checkout.session.type;
        payload.payment_intent_id = this.checkout.stripe.paymentIntent;
        payload.stripe_token = this.checkout.stripe.confirmationToken;

        if (this.checkout.stripe.paymentMethod) {
            payload.stripe_country = this.checkout.stripe.paymentMethod.billing_details?.address?.country || '';
        }

        try {
            const data = await checkoutApi.submitJson(
                this.checkout.form.getAttribute('action'),
                payload,
            );

            this.checkout.placementStage = 'finalising';
            this.awaitOrderCreation(data.session_id);
        } catch (data) {
            if (data.status === 'PAYMENT_REQUIRES_ACTION') {
                const { error, paymentIntent } = await orderSubmission.handlePaymentAction(data.payment.client_secret);

                if (!paymentIntent || error) {
                    this.checkout.stripe = { confirmationToken: '', errorMessage: '', paymentIntent: '', paymentMethod: null };
                    this.order.placed = false;
                    this.checkout.ready = true;
                    this.checkout.failed = {
                        data: { messages: [error?.message], next_url: null, status: 'PAYMENT_FAILED' },
                        status: true,
                    };
                    return;
                }

                this.checkout.stripe.paymentIntent = paymentIntent.id;
                this.checkout.ready = true;

                setTimeout(() => this.submitOrder(), 500);
            } else {
                const action = orderSubmission.decideNextAction(data, { hasPaymentIntent: !!this.checkout.stripe.paymentIntent });

                if (action.type === 'await') {
                    this.checkout.placementStage = 'finalising';
                    this.awaitOrderCreation(action.sessionId || this.checkout.session.id);
                    return;
                }

                this.checkout.ready = true;
                this.order.placed = false;
                this.checkout.failed = { data, status: true };
            }
        }
    },

    awaitOrderCreation(sessionId) {
        orderSubmission.awaitOrderCreation(sessionId, {
            onComplete: (session) => {
                window.location.pathname = new URL(session.redirect_url).pathname;
            },
            onFailed: (data) => {
                this.order.placed = false;
                this.checkout.failed = { data, status: true };
            },
            onTimeout: () => {
                // Payment succeeded; rest on the 'finalising' reassurance copy.
            },
        });
    },
});
